import { useState } from "react";
import type { EnvMatrix } from "@/lib/api";
import { columnLabel } from "./MatrixGrid";

/**
 * Pick any services — one or several, from any table — to compare side by
 * side. Grouped under the tables they usually sit in, so the usual pairs are
 * easy to find and unusual ones (Demo app against Demo Keycloak) easy to make.
 */
export function CustomPicker({
  m,
  initial,
  onCancel,
  onCompare,
}: {
  /** The matrix as the rules files group it, without the custom table. */
  m: EnvMatrix;
  initial: string[];
  onCancel: () => void;
  onCompare: (serviceIds: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial));
  const byId = new Map(m.services.map((s) => [s.id, s]));
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4 text-sm">
      <p className="text-text-secondary">
        Tick the services to compare. This lasts for this browser tab only; nothing is saved.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        {m.tables.map((t) => (
          <fieldset key={t.name} className="space-y-1.5">
            <legend className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
              {t.name}
            </legend>
            {t.serviceIds.map((id) => {
              const s = byId.get(id)!;
              return (
                <label
                  key={id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-token-sm px-2 py-1.5 hover:bg-bg-card-hover"
                >
                  <input
                    id={`custom-${id}`}
                    type="checkbox"
                    checked={picked.has(id)}
                    onChange={() => toggle(id)}
                  />
                  <span className="font-medium text-text-heading">{columnLabel(s)}</span>
                  <span className="truncate font-mono text-xs text-text-muted">{s.name}</span>
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
        {picked.size > 0 && (
          <button
            type="button"
            onClick={() => setPicked(new Set())}
            className="mr-auto text-xs font-medium text-text-muted hover:text-text-heading hover:underline"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="rounded-token-md border border-border bg-bg-card px-4 py-2 text-sm font-medium text-text-heading transition-colors hover:bg-bg-card-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={picked.size === 0}
          onClick={() => onCompare([...picked])}
          className="rounded-token-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Compare {picked.size > 0 ? picked.size : ""} {picked.size === 1 ? "service" : "services"}
        </button>
      </div>
    </div>
  );
}
